import { cached } from '@glimmer/tracking';
import { DateTime } from 'luxon';
import { sortBy } from '../utils/array-helpers';

/**
 * This is an object representation of an event, to be used in the
 * various calendars and week-at-a-glance.
 */
export default class Event {
  /** @var { Boolean } isUserEvent indicates whether this is a user event or a school event */
  isUserEvent;

  /** @var { String } slug the event's slug - its unique identifier. */
  slug;

  /** @var { Boolean } showAsBlockedTime if TRUE, then the event will be styled de-emphasized on the calendar grid. */
  showAsBlockedTime;

  /**
   * @param { Object } data A plain-old JS object, containing all the event's data.
   * @param { Boolean } isUserEvent TRUE if the given object represents a user event, FALSE if it represents a school event.
   * @param { Boolean } showAsBlockedTime TRUE if this is event should be styled de-emphasized on the calendar grid.
   */
  constructor(data, isUserEvent, showAsBlockedTime) {
    this.isUserEvent = isUserEvent;
    this.showAsBlockedTime = showAsBlockedTime;
    // copies all attributes of the given data input to this Event object.
    Object.assign(this, data);
    // set the slug.
    this.slug = this.isUserEvent
      ? this.#getSlugForUserEvent(this.startDate, this.offering, this.ilmSession)
      : this.#getSlugForSchoolEvent(this.startDate, this.school, this.offering, this.ilmSession);
    // converts pre- and post-requisites into Events as well.
    this.prerequisites = sortBy(
      this.prerequisites.map((prereq) => {
        const rhett = new Event(prereq, this.isUserEvent);
        // overwrite the pre-req's start date with its target event's start date.
        rhett.startDate = this.startDate;
        return rhett;
      }),
      ['startDate', 'name'],
    );
    this.postrequisites = sortBy(
      this.postrequisites.map((postreq) => {
        return new Event(postreq, this.isUserEvent);
      }),
      ['startDate', 'name'],
    );
  }

  /**
   * The start-date/time of this event, for display purposes in a calendar.
   * This may differ from its actual start-date/time.
   * @return { String } The event's calendar start-date/time as ISO-formatted text.
   */
  @cached
  get calendarStartDate() {
    // ILMs don't really have a duration, they have due-date.
    // But in order to display them in a calendar, we have to make up a duration for them, otherwise they won't show up.
    // So we're filling in a start-date that's the equivalent of the actual due-date,
    // and give it an end-date that's fifteen minutes out.
    // However, if the given start-date is 11:45p or later in the day, that event would be considered to continue
    // into the next day, effectively making it a "multi-day" event.
    // Multi-days are currently not shown in the calendar, instead they are displayed in a table below the calendar.
    // To prevent this from happening, we pin the calendar display start-date to 11:45p and the end-date to 11:59p.
    if (this.ilmSession) {
      const startDate = DateTime.fromISO(this.startDate);
      if (23 === startDate.hour && 45 <= startDate.minute) {
        return startDate.set({ minute: 45 }).toUTC().toISO();
      }
    }
    return this.startDate;
  }

  /**
   * The end-date/time of this event, for display purposes in a calendar.
   * This may differ from its actual end-date/time.
   * @return { String } The event's calendar end-date/time as ISO-formatted text.
   */
  @cached
  get calendarEndDate() {
    // See comment block inside `Event::calendarStartDate()` for details on this logic.
    if (this.ilmSession) {
      const startDate = DateTime.fromISO(this.startDate);
      if (23 === startDate.hour && 45 <= startDate.minute) {
        return startDate.set({ minute: 59 }).toUTC().toISO();
      }
    }
    return this.endDate;
  }

  /**
   * Whether this event is considered "blanked" or not.
   * @return { Boolean }
   */
  @cached
  get isBlanked() {
    return !this.offering && !this.ilmSession;
  }

  /**
   * Whether this event has prerequisites or not.
   * @return { Boolean }
   */
  get hasPrework() {
    return !!this.prerequisites.length;
  }

  /**
   * Generates a slug from  given user event data.
   * @return { String }
   */
  #getSlugForUserEvent() {
    let slug = 'U';
    slug += DateTime.fromISO(this.startDate).toFormat('yyyyMMdd');
    if (this.offering) {
      slug += 'O' + this.offering;
    }
    if (this.ilmSession) {
      slug += 'I' + this.ilmSession;
    }
    return slug;
  }

  /**
   * Generates a slug for a given school event.
   * @return { String }
   */
  #getSlugForSchoolEvent() {
    let slug = 'S';
    let schoolId = parseInt(this.school, 10).toString();
    //always use a two digit schoolId
    if (schoolId.length === 1) {
      schoolId = '0' + schoolId;
    }
    slug += schoolId;
    slug += DateTime.fromISO(this.startDate).toFormat('yyyyMMdd');
    if (this.offering) {
      slug += 'O' + this.offering;
    }
    if (this.ilmSession) {
      slug += 'I' + this.ilmSession;
    }
    return slug;
  }
}
